/**
 * Utility functions for formatting data display
 */

/**
 * Formats a phone number to display as 123-456-7890
 * Handles various input formats: 1234567890, 123.456.7890, (123) 456-7890, etc.
 * @param {string} phone - The phone number to format
 * @returns {string} Formatted phone number or original value if invalid
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Handle 10-digit US phone numbers
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Handle 11-digit numbers starting with 1 (US country code)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // Return original if not a standard US number
  return phone;
};

/**
 * Formats a date string to a readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Formats a number as currency
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return `$${parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Ordinal form of a day-of-month: 1 -> "1st", 15 -> "15th", 23 -> "23rd".
 * @param {number|string} day
 * @returns {string}
 */
export const formatOrdinalDay = (day) => {
  const n = parseInt(day, 10);
  if (!Number.isFinite(n)) return String(day ?? '');
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

/**
 * Render the tenant's configured payment due days as customer-facing prose.
 * [1, 15]     -> "1st or 15th"
 * [1, 10, 20] -> "1st, 10th or 20th"
 * [5]         -> "5th"
 *
 * Pre-purchase copy uses this so a tenant that offers days other than 1 and 15
 * is not describing terms it does not actually sell.
 *
 * @param {Array<number>} days paymentDueDayOptions from config
 * @returns {string}
 */
export const formatDueDayOptions = (days) => {
  const list = (Array.isArray(days) ? days : []).filter(d => d !== null && d !== undefined);
  if (list.length === 0) return '';
  if (list.length === 1) return formatOrdinalDay(list[0]);
  return `${list.slice(0, -1).map(formatOrdinalDay).join(', ')} or ${formatOrdinalDay(list[list.length - 1])}`;
};

/**
 * When payments fall due for this tenant, as prose, in EITHER mode.
 *
 * A day list is only meaningful in 'offered' mode. In 'anniversary' mode there
 * is no list to render -- the due day is whatever day of the month each buyer
 * closed on -- so every surface that used to print "1st or 15th" has to switch
 * to a sentence instead. This is the single definition of that switch, so a
 * surface cannot be updated for one mode and left describing the other.
 *
 * The anniversary phrasing is a parameter because the audience differs: a buyer
 * reads "your purchase date", an admin reads "the closing date". Everything else
 * about the decision is identical, so it stays one function.
 *
 * @param {string} mode 'offered' or 'anniversary'
 * @param {Array<number>} days paymentDueDayOptions, ignored in anniversary mode
 * @param {string} [anniversaryPhrase]
 * @returns {string}
 */
export const formatDueDayTerms = (mode, days, anniversaryPhrase = 'the anniversary of the purchase date') =>
  (mode === 'anniversary' ? anniversaryPhrase : formatDueDayOptions(days));
